// ANCHOR React
import { useEffect } from 'react';

// ANCHOR Next
import dynamic from 'next/dynamic';
import { GetServerSideProps } from 'next';

// ANCHOR Components
import { LyonAppHead } from '@lyon/components/head/LyonAppHead';
import { FullPageLoader } from '@lyon/components/full-page-loader';

// ANCHOR API
import { getProviderPageGSSP } from '@lyon/utils/api/gssp';
import { IProviderResult } from '@lyon/utils/api/results/provider';

// ANCHOR Graph States
import { useAuthAdmin } from '@lyon/nodes/firebase/auth/auth-admin-node';

// ANCHOR Utils
import { ucarecdn } from '@lyon/utils/ucarecdn';
import { ParsedUrlQuery } from 'querystring';
import { track } from '@lyon/utils/segment';

interface IProps {
  id: string;
  data: IProviderResult;
}
interface IParams extends ParsedUrlQuery {
  pid: string;
}

export const getServerSideProps: GetServerSideProps<IProps, IParams> = async (context) => {
  if (context.params) {
    const id = context.params.pid;

    const data = await getProviderPageGSSP(id);

    if (data) {
      return {
        props: {
          data,
          id,
        },
      };
    }
  }
  return {
    notFound: true,
  };
};

interface IMainProps {
  id: string;
  initialData: IProviderResult;
}

const LyonProvider = dynamic<IMainProps>(
  () => (
    import('@lyon/components/provider/public/LyonProvider')
      .then((mod) => mod.LyonProvider)
  ),
  {
    loading: () => <FullPageLoader />,
  },
);

// eslint-disable-next-line import/no-default-export
export default function LyonProviderPage({ id, data }: IProps) {
  const title = `Lyon | ${data.name}`;

  const isAdmin = useAuthAdmin();

  useEffect(() => {
    track('Learning Space Viewed', {
      trainerId: id,
      trainerName: data.name,
      imageUrl: data.displayPhotoUuid
        ? ucarecdn(data.displayPhotoUuid)
        : undefined,
      url: `https://lyon.com.ph/provider/${id}`,
    }, isAdmin, {
      context: {
        groupId: id,
      },
    });
  }, [data.createdAt, data.displayPhotoUuid, data.name, id, isAdmin]);

  return (
    <>
      <LyonAppHead
        title={title}
        description={data.description}
        openGraph={{
          url: `https://lyon.com.ph/provider/${id}`,
          width: '1366',
          height: '768',
          image: ucarecdn(data.displayPhotoUuid),
        }}
      />
      <LyonProvider id={id} initialData={data} />
    </>
  );
}
